// eslint-disable-next-line no-unused-vars
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import uniqueId from 'lodash/uniqueId';
import { arrayOf, number, oneOfType, shape, string } from 'prop-types';
import { Info, TriangleAlert, Check, Hourglass } from 'lucide-react';

const StyledNotification = styled.div`
  width: 100%;
  position: relative;

  &:after {
    display: ${ props => props.delay ? 'block' : 'none' };
    content: '';
    position: absolute;
    bottom: 5px;
    right: 0;
    height: 2px;
    width: 100%;
    transition: width 10s;
    background: #0F233C;
    animation: linear ${ props => `${ props.delay }s` } delay;
  }

  @keyframes delay {
    from {
      width: 100%;
    }
    to {
      width: 0;
    }
  }

  .messages {
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;

    .message {
      display: flex;
      align-items: center;
      width: 100%;
      margin: 5px 0;
      padding: 10px 20px;
      font-size: 0.875rem;
      font-weight: 500;
      color: ${ props => props.theme.colors.darkGray };
      
      &.success {
        background-color: ${ props => props.theme.products.colors.quickViewButtonBg };

        svg {
          stroke: ${ props => props.theme.colors.green };
        }
      }

      &.error {
        background-color: ${ props => props.theme.colors.lightRed };

        svg {
          stroke: ${ props => props.theme.colors.red };
        }
      }

      &.wait,
      &.info {
        background-color: #DDE6F2;

        svg {
          stroke: #0F233C;
        }
      }

      &.warning {
        background-color: #F4D9C0;

        svg {
          stroke: #F47926;
        }
      }

      svg {
        margin-right: 10px;
        min-width: 25px;
      }

      p {
        color: inherit;
        font-size: inherit;
        line-height: inherit;
        font-weight: inherit;
      }
    }
  }
`;

const NWNotification = ({ className, errors, delay }) => {
  const [show, setShow] = useState(true);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (delay) {
      setShow(true);
      const timer = setTimeout(() => setShow(false), delay * 1000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [errors]);

  const renderIcon = type => {
    if (type === 'error' || type === 'warning') {
      return <TriangleAlert />;
    }

    if (type === 'wait') {
      return <Hourglass />;
    }

    if (type === 'info') {
      return <Info />;
    }

    return <Check />;
  };

  return (show && !!errors.length) && (
    <StyledNotification className={ className } delay={ delay }>
      <div className="messages">
        { errors.map(notification => (
          <div
            key={ uniqueId('order_message_') }
            className={ `message ${ notification.type }` }
            data-test={ `message_${ notification.type }` }
          >
            { renderIcon(notification.type) }
            <p
              key={ uniqueId() }
              dangerouslySetInnerHTML={{
                __html: notification.message
              }}
            />
          </div>
        )) }
      </div>
    </StyledNotification>
  );
};

NWNotification.propTypes = {
  className: string,
  errors: arrayOf(shape({
    message: string,
    type: string,
  })),
  delay: oneOfType([string, number])
};

export { NWNotification };
